import BaseModal from "../layout/BaseModal"
import { ReactComponent as WHEEL } from "../../assets/wheelchair.svg"
import { ReactComponent as TEXTSIZE } from "../../assets/Textgröße 2.svg"
import { ReactComponent as CONTRAST } from "../../assets/Kontrast.svg"
import { ReactComponent as CURSOR } from "../../assets/Cursor.svg"
import { ReactComponent as TRACKING } from "../../assets/Textabstand.svg"
import { ReactComponent as SOUND } from "../../assets/Sound.svg"
import { ReactComponent as EASY } from "../../assets/Information.svg"
import BaseHeadline from "./BaseHeadline"
import BaseSubheadline from "./BaseSubheadline"
import { ReactNode, useContext } from "react"
import { useAccessibility } from "../../context/AccessibilityContext"
import PrimaryButton from "./PrimaryButton"
import IconButton from "./IconButton"
import { speakOutLoud } from "../../services/functions"
import { ThemeContext } from "../../context/ThemeContext"
import { useNavigate } from "react-router-dom"
import { API } from "../../constants/constants"
import NavigationContentQuery from "../../api/generalQuerys"
import { Checkbox } from "react-daisyui"
import { ModalContext } from "../../context/ModalContext"

export default function AccessibilityModal() {
    const {
        cycleTextSize,
        contrast,
        setContrast,
        cycleCursorSize,
        cycleTextSpacing,
        cycleEasySpeech,
        easySpeech,
        cycleTextread,
        settings,
        readText,
        displayedTextSizeLevel,
    } = useAccessibility()
    const themeColors = useContext(ThemeContext)
    const navigate = useNavigate()
    const { navigationContent, navigationContentError, navigationContentIsLoading } = NavigationContentQuery()
    const modalCon = useContext(ModalContext)

    return (
        <BaseModal modalId={0}>
            {!navigationContentIsLoading && !navigationContentError && navigationContent && (
                <img
                    className="hidden sm:block h-20 w-fit max-w-[8rem] object-contain absolute top-16 left-16"
                    src={API + navigationContent?.content?.navigationLogo?.url}
                    alt={
                        navigationContent?.content?.navigationLogo?.description ??
                        navigationContent?.content?.navigationLogo?.name ??
                        "logo_in_header"
                    }
                />
            )}
            <div className="flex flex-col w-full h-full items-center justify-center bg-base-200 text-neutral pt-24">
                <WHEEL className="w-12 h-12 text-secondary mb-4" />
                <BaseHeadline
                    headlineType={4}
                    label={"Herzlich Willkommen!"}
                    classProps="pt-2 text-center"
                    customColor={themeColors?.color?.bg2 === themeColors?.color?.text ? "#f7f7f7" : undefined}
                />
                <BaseSubheadline
                    label="Bei den Halleschen Behindertenwerkstätten e. V."
                    classProps="text-center"
                    customColor={themeColors?.color?.bg2 === themeColors?.color?.text ? "#f7f7f7" : undefined}
                />
                <p className="py-8 text-center max-w-xl">
                    Für eine optimale Nutzung bieten wir Ihnen die Möglichkeit, diese Website an Ihre individuellen Bedürfnisse anzupassen. Entdecken
                    Sie unsere Barrierefreiheit-Tools, um die Seite Ihren Wünschen entsprechend anzupassen.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full sm:w-4/5 items-center justify-center">
                    <AccessibilityBox
                        label="Kontrast"
                        icon={<CONTRAST className="w-8 h-8 " />}
                        clicked={contrast}
                        function={() => setContrast(!contrast)}
                    />
                    <AccessibilityBox
                        label="Textgröße"
                        icon={<TEXTSIZE className="w-8 h-8 " />}
                        function={cycleTextSize}
                        step={displayedTextSizeLevel}
                    />
                    <AccessibilityBox
                        label="Cursor"
                        icon={<CURSOR className="w-8 h-8 " />}
                        function={cycleCursorSize}
                        step={settings.cursorSize + 1}
                    />
                    <AccessibilityBox
                        label="Text Abstand"
                        icon={<TRACKING className="w-8 h-8 " />}
                        function={cycleTextSpacing}
                        step={settings.textSpacing + 1}
                    />
                    <AccessibilityBox label="Einfache Sprache" icon={<EASY className="w-8 h-8 " />} clicked={easySpeech} function={cycleEasySpeech} />
                    <AccessibilityBox label="Vorlesen" icon={<SOUND className="w-8 h-8 " />} clicked={readText} function={cycleTextread} />
                </div>
                <div className="flex gap-8 pt-12 items-center">
                    <PrimaryButton
                        text={"Zur Startseite"}
                        onClick={() => {
                            modalCon?.trigger(0)
                            navigate("/homepage")
                        }}
                        active
                        soundHidden={true}
                    />
                    <IconButton
                        function={() =>
                            speakOutLoud(
                                "Für eine optimale Nutzung bieten wir Ihnen die Möglichkeit, diese Website an Ihre individuellen Bedürfnisse anzupassen. Entdecken Sie unsere Barrierefreiheit-Tools, um die Seite Ihren Wünschen entsprechend anzupassen."
                            )
                        }
                    />
                </div>
            </div>
        </BaseModal>
    )
}

type AccessibilityBoxType = {
    label: string
    icon: ReactNode
    function: () => void
    clicked?: boolean
    step?: number
}

function AccessibilityBox(props: AccessibilityBoxType) {
    const { getCursorPointerClass } = useAccessibility()

    return (
        <div
            className={`w-full h-20 sm:h-fit p-4 rounded-default shadow-md bg-white flex flex-row items-center justify-between ${getCursorPointerClass()}`}
            onClick={props.function}>
            <div className="flex gap-8 items-center">
                {props.icon}
                <p className="font-semibold">{props.label}</p>
            </div>
            {props.step ? (
                <p className="font-semibold border py-0.5 px-2.5 rounded-lg">{props.step}</p>
            ) : (
                <Checkbox checked={props.clicked} className="checkbox checkbox-primary" />
            )}
        </div>
    )
}
